import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { BASE_URL } from "../store/urls";
import { CREATE_UPDATE_MENU } from "../apollo/Mutations";
import { GET_MENU_BY_RESTAURANT } from "../apollo/Queries";

export default function AddMenuModal({ isOpen, onClose, restaurantUuid, initialData }) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue
    } = useForm();

    const [uploading, setUploading] = useState(false);
    const [responseMsg, setResponseMsg] = useState("");
    const [photos, setPhotos] = useState([]);

    const [createUpdateMenu, { loading }] = useMutation(CREATE_UPDATE_MENU, {
        refetchQueries: [{ query: GET_MENU_BY_RESTAURANT, variables: { uuid: restaurantUuid } }]
    });

    useEffect(() => {
        if (initialData) {
            Object.keys(initialData).forEach(key => {
                if (key === 'description') {
                    setValue('description1', initialData[key]);
                } else if (key === 'uuid') {
                    setValue('uuid1', initialData[key]);
                } else if (key === 'ingredients' && Array.isArray(initialData[key])) {
                    setValue(key, initialData[key].join(', '));
                } else {
                    setValue(key, initialData[key]);
                }
            });
            setPhotos(initialData.photos || []);
        } else {
            reset();
            setPhotos([]);
        }
    }, [initialData, setValue, reset, isOpen]);

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        try {
            setUploading(true);
            const uploadedPhotos = [...photos];

            for (const file of files) {
                const formData = new FormData();
                formData.append("file", file);

                const response = await fetch(`${BASE_URL}/files/upload/image`, {
                    method: "POST",
                    body: formData,
                });
                const result = await response.json();
                if (result.data) {
                    uploadedPhotos.push(result.data);
                }
            }

            setPhotos(uploadedPhotos);
            setValue("photos", uploadedPhotos);
        } catch (err) {
            console.error("File upload failed:", err);
            alert("Failed to upload images.");
        } finally {
            setUploading(false);
        }
    };

    const removePhoto = (index) => {
        const newPhotos = photos.filter((_, i) => i !== index);
        setPhotos(newPhotos);
        setValue("photos", newPhotos);
    };

    const submitHandler = async (formData) => {
        try {
            const variables = {
                ...formData,
                ingredients: typeof formData.ingredients === 'string'
                    ? formData.ingredients.split(',').map(s => s.trim())
                    : formData.ingredients,
                photos: photos,
                restaurantUuid: restaurantUuid,
                uuid1: initialData?.uuid || "",
                description1: formData.description1 || ""
            };

            const { data } = await createUpdateMenu({ variables });

            if (data?.createUpdateMenu?.error) {
                setResponseMsg(`❌ ${data.createUpdateMenu.message}`);
            } else {
                setResponseMsg(`✅ Menu ${initialData ? 'updated' : 'added'} successfully`);
                setTimeout(() => {
                    setResponseMsg("");
                    reset();
                    onClose();
                }, 1500);
            }
        } catch (error) {
            console.error("GraphQL error:", error);
            setResponseMsg("❌ Failed to save menu item.");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md">
            <div className="bg-white w-full max-w-lg p-8 rounded-[2.5rem] shadow-2xl relative animate-scale-in">
                <h2 className="text-2xl font-black text-premium-teal mb-6">
                    {initialData ? "Add Menus" : "Add New Menu"}
                </h2>

                <form onSubmit={handleSubmit(submitHandler)} className="space-y-6">
                    <div className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Dish Title</label>
                            <input {...register("title", { required: "Title is required" })} placeholder="e.g. Truffle Glazed Salmon" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
                            {errors.title && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1">{errors.title.message}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Price (USD)</label>
                                <input {...register("price", { required: "Price is required" })} placeholder="25.00" type="text" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Quantity</label>
                                <input {...register("quantity")} placeholder="1" type="number" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Ingredients</label>
                            <input {...register("ingredients")} placeholder="Truffle, Salmon, Asparagus..." className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all" />
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Description</label>
                            <textarea {...register("description1")} placeholder="Describe the flavor profiles..." rows="3" className="w-full border-gray-100 bg-gray-50/50 p-3 rounded-xl focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all resize-none" />
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Gallery</label>
                            <div className="flex flex-wrap gap-2 mb-2">
                                {photos.map((photo, index) => (
                                    <div key={index} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-100">
                                        <img src={`${BASE_URL}${photo}`} alt="" className="w-full h-full object-cover" />
                                        <button type="button" onClick={() => removePhoto(index)} className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl-lg hover:bg-red-600 transition-colors">
                                            <svg size={10} fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-3 h-3"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                    </div>
                                ))}
                                <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:border-premium-gold/50 transition-colors">
                                    <input type="file" multiple accept="image/*" onChange={handleFileChange} className="hidden" />
                                    <span className="text-premium-gold text-lg">+</span>
                                </label>
                            </div>
                            {uploading && <div className="text-[10px] font-bold text-premium-gold animate-pulse uppercase tracking-widest">Uploading Assets...</div>}
                        </div>
                    </div>

                    <div className="pt-4 flex items-center justify-between">
                        <div className="flex-1">
                            {responseMsg && (
                                <p className={`text-[10px] font-black uppercase tracking-widest animate-fade-in ${responseMsg.includes('✅') ? 'text-green-500' : 'text-red-500'}`}>
                                    {responseMsg}
                                </p>
                            )}
                        </div>

                        <div className="flex gap-4">
                            <button type="button" onClick={onClose} className="px-6 py-3 bg-gray-50 text-gray-400 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-100 transition-all">
                                Cancel
                            </button>
                            <button type="submit" disabled={loading || uploading} className={`px-8 py-3 rounded-xl text-white font-black text-[10px] uppercase tracking-widest shadow-lg transition-all flex items-center gap-2 ${loading || uploading ? "bg-premium-teal/50 cursor-not-allowed" : "bg-premium-teal hover:shadow-premium-teal/20"}`}>
                                {loading && <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>}
                                {initialData ? "Refine" : "Commit"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
